import { createSelector } from 'reselect';

import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type RootState from 'app-components/utils/redux-store/rootState';

import type { AiChatState, ChatAgentValue, ConversationData } from './aiChatTypes';
import { AI_CHAT_REDUCER_KEY, ChatAgents, DEFAULT_AGENT } from './aiChatTypes';

type StateWithAiChat = { aiChatStore: AiChatState };

const selectAiChatState = (state: StateWithAiChat): AiChatState => state[AI_CHAT_REDUCER_KEY];

export const selectConversation = (state: StateWithAiChat, conversationId: string): ConversationData | null =>
  selectAiChatState(state).conversationsData[conversationId] ?? null;

export const selectConversationIsEmpty = (state: StateWithAiChat, conversationId: string): boolean =>
  (selectAiChatState(state).conversationsData[conversationId]?.messages.length ?? 0) === 0;

export const selectConversationHasMore = (state: StateWithAiChat, conversationId: string): boolean =>
  selectAiChatState(state).conversationsData[conversationId]?.hasMoreItems ?? false;

export const selectConversationIsLoadingMore = (state: StateWithAiChat, conversationId: string): boolean =>
  selectAiChatState(state).conversationsData[conversationId]?.isLoadingMore ?? false;

export const getConversationsList = (state: StateWithAiChat) => selectAiChatState(state).conversationsList;

export const getCurrentConversationId = (state: StateWithAiChat) => selectAiChatState(state).currentConversationId;
export const isCurrentConversationEmpty = (state: StateWithAiChat): boolean => {
  const conversationId = getCurrentConversationId(state);
  return conversationId ? selectConversationIsEmpty(state, conversationId) : true;
};

export const isLoadingConversations = (state: StateWithAiChat) => selectAiChatState(state).isLoadingConversations;
export const isFetchingInitialConversations = (state: StateWithAiChat) =>
  selectAiChatState(state).isFetchingInitialConversations;
export const hasMoreConversations = (state: StateWithAiChat) => selectAiChatState(state).conversationsMeta.hasMore;
export const getConversationsPage = (state: StateWithAiChat) => selectAiChatState(state).conversationsMeta.page;
export const getChatUIState = (state: StateWithAiChat) => selectAiChatState(state).chatUIState;
export const getChatOpenPayload = (state: StateWithAiChat) => selectAiChatState(state).chatOpenPayload;
export const isChatOpen = (state: StateWithAiChat) => selectAiChatState(state).chatUIState === 'open';
export const isChatMinimized = (state: StateWithAiChat) => selectAiChatState(state).chatUIState === 'minimized';
export const getMinimizedPreview = (state: StateWithAiChat) => selectAiChatState(state).minimizedPreview;
export const getError = (state: StateWithAiChat) => selectAiChatState(state).error;

type StateWithAiChatAndEntitlements = StateWithAiChat & Parameters<typeof entitlementsSelectors.getBusinessEnabled>[0];

export const getDefaultChatAgent = (state: StateWithAiChatAndEntitlements): ChatAgentValue =>
  entitlementsSelectors.getIsQBP(state) ? ChatAgents.QBP_RESPONSES_CHAT : DEFAULT_AGENT;

const selectIsAiBusinessToolsReleased = (state: RootState): boolean =>
  !!featureFlagsSelectors.getFeatureFlag(state, 'aiBusinessToolsRelease');

export const selectIsAiBusinessToolsEnabled = createSelector(
  [entitlementsSelectors.getBusinessEnabled, selectIsAiBusinessToolsReleased],
  (hasBusinessEntitlement, isReleased) => hasBusinessEntitlement && isReleased,
);

export const selectShouldHideBusinessFromAiChat = createSelector(
  [entitlementsSelectors.getBusinessEnabled, selectIsAiBusinessToolsReleased],
  (hasBusinessEntitlement, isReleased) => hasBusinessEntitlement && !isReleased,
);
